import React, { useState, useEffect, useMemo } from 'react';
import { useAdminSubscriptions } from '@/hooks/supabase/useAdminSubscriptions';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { Input } from '@/components/ui/input';
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

const SubscriptionManager = () => {
  const { 
    subscriptions, 
    loading, 
    error, 
    refresh,
    freeUsers,
    freeUsersCount,
    freeUsersLoading,
    fetchFreeUsers
  } = useAdminSubscriptions();

  const PAGE_SIZE = 50;

  // State for Free Users table
  const [freeUsersSearchTerm, setFreeUsersSearchTerm] = useState('');
  const [debouncedFreeUsersSearchTerm, setDebouncedFreeUsersSearchTerm] = useState('');
  const [freeUsersPage, setFreeUsersPage] = useState(0);

  // State for Subscribed Users table
  const [subscribedSearchTerm, setSubscribedSearchTerm] = useState('');
  const [debouncedSubscribedSearchTerm, setDebouncedSubscribedSearchTerm] = useState('');
  const [subscribedPage, setSubscribedPage] = useState(0);

  // Track desired plan per user for granting access
  const [grantPlanByUser, setGrantPlanByUser] = useState<Record<string, 'basic' | 'standard' | 'enterprise'>>({});

  const setUserGrantPlan = (userId: string, plan: 'basic' | 'standard' | 'enterprise') => {
    setGrantPlanByUser(prev => ({ ...prev, [userId]: plan }));
  };

  const grantAccess = async (userId: string) => {
    const plan = grantPlanByUser[userId] || 'standard';

    try {
      // Ensure user doesn't already have an active subscription
      const { data: existing, error: existingError } = await supabase
        .from('user_subscriptions')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();

      if (existingError) {
        throw existingError;
      }
      if (existing) {
        toast({ title: 'Already Subscribed', description: 'This user already has an active subscription.', variant: 'destructive' });
        return;
      }

      const { error: insertError } = await supabase
        .from('user_subscriptions')
        .insert({
          user_id: userId,
          plan_id: plan,
          status: 'active',
          started_at: new Date().toISOString(),
          ends_at: null,
          purchase_id: null,
        });

      if (insertError) {
        throw insertError;
      }

      toast({ title: 'Access Granted', description: `User has been granted ${plan} plan access.` });

      // Refresh both tables
      await refresh();
      await fetchFreeUsers(freeUsersPage, PAGE_SIZE, debouncedFreeUsersSearchTerm);
    } catch (e: any) {
      toast({ title: 'Grant Failed', description: e.message || 'Could not grant access.', variant: 'destructive' });
    }
  };

  // Debounce effect for Free Users search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFreeUsersSearchTerm(freeUsersSearchTerm);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [freeUsersSearchTerm]);

  // Fetch Free Users data based on debounced search and page
  useEffect(() => {
    fetchFreeUsers(freeUsersPage, PAGE_SIZE, debouncedFreeUsersSearchTerm);
  }, [fetchFreeUsers, freeUsersPage, debouncedFreeUsersSearchTerm]);

  // Debounce effect for Subscribed Users search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSubscribedSearchTerm(subscribedSearchTerm);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [subscribedSearchTerm]);

  // Filter and paginate subscribed users client-side
  const filteredAndPaginatedSubscriptions = useMemo(() => {
    if (!subscriptions) return [];

    const filtered = subscriptions.filter(sub => {
      const fullName = sub.profiles?.full_name?.toLowerCase() || '';
      const email = sub.profiles?.email?.toLowerCase() || '';
      const term = debouncedSubscribedSearchTerm.toLowerCase();
      return fullName.includes(term) || email.includes(term);
    });

    const start = subscribedPage * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filtered.slice(start, end);
  }, [subscriptions, subscribedPage, debouncedSubscribedSearchTerm]);

  const totalSubscribedPages = useMemo(() => {
    if (!subscriptions) return 0;
    const filtered = subscriptions.filter(sub => {
      const fullName = sub.profiles?.full_name?.toLowerCase() || '';
      const email = sub.profiles?.email?.toLowerCase() || '';
      const term = debouncedSubscribedSearchTerm.toLowerCase();
      return fullName.includes(term) || email.includes(term);
    });
    return Math.ceil(filtered.length / PAGE_SIZE);
  }, [subscriptions, debouncedSubscribedSearchTerm]);

  const analytics = useMemo(() => {
    if (!subscriptions) return { total: 0, byTier: {} };
    const byTier = subscriptions.reduce((acc, sub) => {
      const tier = sub.plan_id || 'unknown';
      acc[tier] = (acc[tier] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return {
      total: subscriptions.length,
      byTier,
    };
  }, [subscriptions]);

  const totalFreePages = Math.ceil(freeUsersCount / PAGE_SIZE);

  const handleFreeSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFreeUsersSearchTerm(e.target.value);
    setFreeUsersPage(0); // Reset page on new search
  };

  const handleSubscribedSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubscribedSearchTerm(e.target.value);
    setSubscribedPage(0); // Reset page on new search
  };

  const renderSubscribedUsers = () => {
    if (loading) {
      return (
        <div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </div>
          <Card>
            <CardHeader>
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-96" />
            </CardContent>
          </Card>
        </div>
      );
    }

    if (error) {
      return <p className="text-destructive">Error loading subscriptions: {error.message}</p>;
    }

    return (
      <>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Subscriptions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.total}</div>
            </CardContent>
          </Card>
          {Object.entries(analytics.byTier).map(([tier, count]) => (
            <Card key={tier}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium capitalize">{tier} Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{count}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Subscribed Users</CardTitle>
            <CardDescription>A list of all users with an active subscription.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Input
                placeholder="Search by name or email..."
                value={subscribedSearchTerm}
                onChange={handleSubscribedSearchChange}
                className="max-w-sm"
              />
            </div>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndPaginatedSubscriptions.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell>{sub.profiles?.full_name || 'N/A'}</TableCell>
                      <TableCell>{sub.profiles?.email || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">{sub.plan_id || 'N/A'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className="capitalize bg-green-500 hover:bg-green-600">{sub.status}</Badge>
                      </TableCell>
                      <TableCell>
                        {sub.started_at ? format(new Date(sub.started_at), 'PP') : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {sub.ends_at ? format(new Date(sub.ends_at), 'PP') : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Page {subscribedPage + 1} of {totalSubscribedPages}
              </div>
              <Pagination className="mx-0 w-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setSubscribedPage(p => Math.max(0, p - 1));
                      }}
                      className={subscribedPage === 0 ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setSubscribedPage(p => (p + 1 < totalSubscribedPages ? p + 1 : p));
                      }}
                      className={subscribedPage + 1 >= totalSubscribedPages ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </CardContent>
        </Card>
      </>
    );
  };

  const renderFreeUsers = () => (
    <Card>
      <CardHeader>
        <CardTitle>Free Plan Users</CardTitle>
        <CardDescription>
          Showing {freeUsers.length} of {freeUsersCount} users without an active subscription.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <Input
            placeholder="Search by name or email..."
            value={freeUsersSearchTerm}
            onChange={handleFreeSearchChange}
            className="max-w-sm"
          />
        </div>
        {freeUsersLoading ? (
          <Skeleton className="h-96" />
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Joined Date</TableHead>
                    <TableHead className="text-right">Grant Access</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {freeUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.full_name || 'N/A'}</TableCell>
                      <TableCell>{user.email || 'N/A'}</TableCell>
                      <TableCell>
                        {user.created_at ? format(new Date(user.created_at), 'PP') : 'N/A'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Select
                            value={grantPlanByUser[user.id] || 'standard'}
                            onValueChange={(val) => setUserGrantPlan(user.id, val as 'basic' | 'standard' | 'enterprise')}
                          >
                            <SelectTrigger className="w-36">
                              <SelectValue placeholder="Select plan" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="basic">Basic</SelectItem>
                              <SelectItem value="standard">Standard</SelectItem>
                              <SelectItem value="enterprise">Enterprise</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="default" onClick={() => grantAccess(user.id)}>
                            Grant
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Page {freeUsersPage + 1} of {totalFreePages}
              </div>
              <Pagination className="mx-0 w-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setFreeUsersPage(p => Math.max(0, p - 1));
                      }}
                      className={freeUsersPage === 0 ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setFreeUsersPage(p => (p + 1 < totalFreePages ? p + 1 : p));
                      }}
                      className={freeUsersPage + 1 >= totalFreePages ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Subscriptions Management</h2>
        <Button onClick={() => refresh()} variant="outline" size="icon" disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      {renderSubscribedUsers()}
      {renderFreeUsers()}
    </div>
  );
};

export default SubscriptionManager;